import { useCallback, useContext, useState } from "react";
import { useModal } from "react-modal-hook";
import { useHistory } from "react-router";
import { OBJECT_TYPE } from "common/constants";
import { buildLink } from "common/routing";
import {
  formatSearchResultAddress,
  SearchResultProps,
} from "common-client/utils/search";
import {
  useCreateWorkflowMutation,
  useGetOrCreatePropertyLazyQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { colors, cornerRadius, spacing, styled } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { CommonModal } from "../Common/CommonModal";
import { Icon } from "../Common/Icons/LucideIcons";
import Modal from "../Common/Modal";
import { Body } from "../Common/Typography";
import Label from "../Inputs/Label";
import { OmniboxSearch } from "../Search/OmniboxSearch";
import { SearchContextProvider } from "../Search/SearchContext";

const SelectedProperty = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  width: "100%",
  border: `1px solid ${colors.borderUi}`,
  borderRadius: cornerRadius.radiusSm,
  padding: `${spacing.s} ${spacing.m}`,
  backgroundColor: colors.bgUiSurface,
});

export type AddWorkflowModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  workflowDefinitionId: string;
};

export const AddWorkflowModal = ({
  onSuccess,
  onCancel,
  workflowDefinitionId,
}: AddWorkflowModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin } = useContext(AuthContext);
  const history = useHistory();
  const [selectedProperty, setSelectedProperty] = useState<
    Maybe<{
      id: string;
      address: string;
    }>
  >(null);

  const [createWorkflowMutation, { loading }] = useCreateWorkflowMutation({
    onCompleted: data => {
      addSuccessToast("Workflow created successfully");
      track("Workflow created", {
        workflowId: data.createWorkflow.id,
        workflowDefinitionId,
        propertyId: selectedProperty?.id,
        userId: user?.id ?? admin?.id,
        userEmail: user?.email ?? admin?.email,
      });

      const workflowUrl = buildLink("workflow", {
        workflowId: data.createWorkflow.id,
      });
      history.push(workflowUrl);
      onSuccess();
    },
    onError: () => {
      addErrorToast(
        `There was an issue creating your workflow. Please try again or contact support@withforerunner.com`,
      );
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!selectedProperty?.id || !workflowDefinitionId) {
      return;
    }

    await createWorkflowMutation({
      variables: {
        data: {
          workflowDefinitionId,
          objectId: selectedProperty.id,
          objectType: OBJECT_TYPE.PROPERTY,
        },
      },
    });
  }, [createWorkflowMutation, selectedProperty, workflowDefinitionId]);

  const [getOrCreateProperty, { loading: getOrCreatePropertyLoading }] =
    useGetOrCreatePropertyLazyQuery({
      onError: () => {
        addErrorToast(
          "There was an issue finding a property with this address. Please provide a different address",
        );
      },
    });

  const handlePropertySelect = useCallback(
    async (result: SearchResultProps) => {
      if (result.propertyId) {
        setSelectedProperty({
          id: result.propertyId,
          address: result.address ?? "",
        });
        return;
      }

      const { data } = await getOrCreateProperty({
        variables: {
          address: result.address,
          latitude: result.point.latitude,
          longitude: result.point.longitude,
          parcelId: result.geocacheParcelId,
          improveParcelCoordinates: true,
        },
      });

      if (data?.getProperty) {
        setSelectedProperty({
          id: data.getProperty.id,
          address: data.getProperty.fullAddress ?? result.address ?? "",
        });
      } else {
        addErrorToast(
          "There was an issue finding a property with this address. Please provide a different address",
        );
      }
    },
    [addErrorToast, getOrCreateProperty],
  );

  return (
    <CommonModal
      title="Add new workflow"
      content={
        <FlexColumn gap="s">
          <Label text="Address" required />
          {selectedProperty ?
            <SelectedProperty>
              <Icon iconName="search" color="contentSecondary" size="16" />
              <Body size="default" type="regular" style={{ flexGrow: 1 }}>
                {formatSearchResultAddress(selectedProperty.address)}
              </Body>
              <Icon
                iconName="x"
                color="contentSecondary"
                size="16"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedProperty(null);
                }}
              />
            </SelectedProperty>
          : <SearchContextProvider
              handleSearchResultClick={handlePropertySelect}
            >
              <OmniboxSearch compactSearchPanel outline overlaySearchPanel />
            </SearchContextProvider>
          }
        </FlexColumn>
      }
      onCancel={onCancel}
      loading={loading || getOrCreatePropertyLoading}
      size="small"
      dataTestId="add-workflow-modal"
      onSubmit={handleSubmit}
      primaryButtonText="Add"
      disabled={!selectedProperty || !workflowDefinitionId}
    />
  );
};

export const useAddWorkflowModal = (workflowDefinitionId: string) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <AddWorkflowModal
          onCancel={hideModal}
          onSuccess={hideModal}
          workflowDefinitionId={workflowDefinitionId}
        />
      </Modal>
    ),
    [workflowDefinitionId],
  );

  return [showModal, hideModal] as const;
};
